import React, { useEffect, useState } from "react";
import { Box, Typography, useTheme, CircularProgress, TextField, MenuItem } from "@mui/material";
import styles from "../../features/Chatbot/styles";
import { fetchAllProjectsRecursive, fetchAllSites, fetchAllUnitsForSiteRecursive } from "../../services/apis";
import { useQuery } from "@tanstack/react-query";
import { SelectItem } from "../../types/Chatbot/Chat";
import paceAiLogo from "../../assets/Images/ChatAvatar.svg";
import { isEqualOrBelowPACE4 } from "../../utils/unitDeviceVersion";

interface UnitSelectionFormProps {
  onChatClick: (project: SelectItem | null, site: SelectItem | null, unit: SelectItem | null, timeframe: SelectItem | null) => void;
  onStartNewConversation: any;
  setOnStartNewConversation: any;
  setNoAlertsFound: any;
  onSelectionChange?: () => void;
}

const UnitSelectionForm: React.FC<UnitSelectionFormProps> = ({ onChatClick, onStartNewConversation, setOnStartNewConversation, setNoAlertsFound, onSelectionChange }) => {
  const theme = useTheme();
  const [project, setProject] = useState<SelectItem | null>(null);
  const [site, setSite] = useState<SelectItem | null>(null);
  const [unit, setUnit] = useState<SelectItem | null>(null);
  const [timeframe, setTimeframe] = useState<SelectItem | null>(null);
  const [timeframes] = useState<SelectItem[]>([
    { id: 3, name: 3 },
    { id: 6, name: 6 },
  ]);

  // const handleChatClick = () => {
  //   if (project && site && unit && timeframe) {
  //   }
  // };

  useEffect(() => {
    if (onStartNewConversation) {
      setProject(null);
    }
  }, [onStartNewConversation]);

  useEffect(() => {
    // if (project && site && unit && timeframe) {
    // send the selected values to the parent component
    onChatClick(project, site, unit, timeframe);
    // }
  }, [project, site, unit, timeframe]);

  const { data: projects, isLoading: isProjectsLoading, isError: isProjectsError, error: projectsError } = useQuery({ queryKey: ["projects"], queryFn: fetchAllProjectsRecursive });

  const {
    data: sites,
    isLoading: isSitesLoading,
    isError: isSitesError,
    error: sitesError,
  } = useQuery({ queryKey: ["sites", { projectId: project?.id }], queryFn: fetchAllSites, enabled: !!project });

  const {
    data: units,
    isLoading: isUnitsLoading,
    isError: isUnitsError,
    error: unitsError,
  } = useQuery({
    queryKey: ["units", { siteId: site?.id }],
    queryFn: ({ queryKey }) => fetchAllUnitsForSiteRecursive((queryKey[1] as { siteId: string }).siteId),
    enabled: !!site,
  });

  const renderDropdown = (label: string, header: string, value: any, options: any[], onChange: (e: any) => void, isLoading: boolean, disabled: boolean) => {
    // console.log(options, "options");
    return (
      <Box sx={styles.answerBox}>
        <Box sx={styles.avatarIcon}>
          <img src={paceAiLogo} alt="icon" width="40" height="40" />
          <Typography variant="body1" sx={styles.messageUser}>
            PACE AI
          </Typography>
        </Box>
        <Typography variant="h4" sx={styles.messageHeader}>
          {header}
        </Typography>
        <TextField select label={label} value={value || ""} onChange={onChange} sx={styles.textField(theme)} variant="outlined" disabled={disabled || isLoading}>
          {isLoading ? (
            <MenuItem disabled>Loading...</MenuItem>
          ) : options.length > 0 ? (
            (() => {
              switch (label) {
                case "Project":
                  return options.map((option: any, idx: number) => (
                    <MenuItem key={idx} value={option.id}>
                      {option.name}
                    </MenuItem>
                  ));

                case "Site":
                  return options.map((option: any, idx: number) => (
                    <MenuItem key={idx} value={option.site_id}>
                      {option.site_name}
                    </MenuItem>
                  ));

                case "Unit":
                  return options.map((option: any, idx: number) => (
                    <MenuItem key={idx} value={option.unit_name}>
                      {option.unit_name}
                    </MenuItem>
                  ));

                default:
                  return options.map((option: any, idx: number) => (
                    <MenuItem key={idx} value={option.id}>
                      {option.name}
                    </MenuItem>
                  ));
              }
            })()
          ) : (
            <MenuItem disabled>No options available</MenuItem>
          )}
        </TextField>
      </Box>
    );
  };

  return (
    <>
      {isProjectsError && <p>Error fetching projects: {projectsError.message}</p>}
      {isSitesError && <p>Error fetching sites: {sitesError.message}</p>}
      {isUnitsError && <p>Error fetching units: {unitsError.message}</p>}

      {projects &&
        Array.isArray(projects) &&
        projects.length > 0 &&
        renderDropdown(
          "Project",
          "Which project are you interested in?",
          project?.id,
          projects.filter((p) => p.pace5_device_count > 0).map((p) => ({ id: p.project_id, name: p.name })),
          (e) => {
            const selected = projects.find((p) => p.project_id === e.target.value);
            setProject(selected ? { id: selected.project_id, name: selected.name } : null);
            setSite(null);
            setUnit(null);
            setOnStartNewConversation(false);
            setTimeframe(null);
            setNoAlertsFound(false);
            onSelectionChange?.();
          },
          isProjectsLoading,
          false
        )}

      {sites?.data?.length > 0 &&
        !onStartNewConversation &&
        renderDropdown(
          "Site",
          "Which site are you interested in?",
          site?.id,
          sites.data.filter((s: any) => s.pace5_device_count > 0),
          (e) => {
            // console.log("sites", sites);
            const selected = sites.data.find((s: any) => s.site_id === e.target.value);
            setSite(selected ? { id: selected.site_id, name: selected.site_name } : null);
            setUnit(null);
            setTimeframe(null);
            setNoAlertsFound(false);
            onSelectionChange?.();
          },
          isSitesLoading,
          !project
        )}

      {units?.data &&
        Array.isArray(units?.data) &&
        !onStartNewConversation &&
        units?.data?.length > 0 &&
        renderDropdown(
          "Unit",
          "Which unit are you interested in?",
          unit?.name,
          units?.data?.filter((u: any) => !isEqualOrBelowPACE4(u.pace_version)),
          (e) => {
            const selected = units?.data?.find((u: any) => u.unit_name === e.target.value);
            setUnit(selected ? { id: selected.unit_id, name: selected.unit_name } : null);
            setNoAlertsFound(false);
            onSelectionChange?.();
          },
          isUnitsLoading,
          !site
        )}

      {unit &&
        timeframes.length > 0 &&
        !onStartNewConversation &&
        renderDropdown(
          "Timeframe",
          "Select a timeframe in months",
          timeframe?.id,
          timeframes,
          (e) => {
            const selected = timeframes.find((t) => t.id === e.target.value);
            setTimeframe(selected ? { id: selected.id, name: selected.name } : null);
            setNoAlertsFound(false);
            onSelectionChange?.();
          },
          false,
          false
        )}

      <Box sx={{ justifyContent: "center", alignItems: "center", width: "100%", display: "flex" }}>{(isProjectsLoading || isSitesLoading || isUnitsLoading) && <CircularProgress />}</Box>
    </>
  );
};

export default UnitSelectionForm;
